import type { Colors, Color } from './types';

const white = '#FFFFFF';
const whiteCSSVariable = '--color__base--white';

const black = '#000000';
const blackCSSVariable = '--color__base--black';

const green: Color = {
  '100': '#ebffee',
  '200': '#cff7d3',
  '300': '#aff4c6',
  '400': '#85e0a3',
  '500': '#14ae5c',
  '600': '#009951',
  '700': '#008043',
  '800': '#036838',
  '900': '#024626',
  '1000': '#083a23',
};

const greenCSSVariables: Color = {
  '100': '--color__base--green100',
  '200': '--color__base--green200',
  '300': '--color__base--green300',
  '400': '--color__base--green400',
  '500': '--color__base--green500',
  '600': '--color__base--green600',
  '700': '--color__base--green700',
  '800': '--color__base--green800',
  '900': '--color__base--green900',
  '1000': '--color__base--green1000',
};

// Values come Yellow family on International DS
const orange: Color = {
  '100': '#fff4e5',
  '200': '#ffe0c2',
  '300': '#fcd19c',
  '400': '#ffc470',
  '500': '#ffa629',
  '600': '#fc9e24',
  '700': '#f79722',
  '800': '#dd7c0e',
  '900': '#ce7012',
  '1000': '#8a480f',
};

const orangeCSSVariables: Color = {
  '100': '--color__base--orange100',
  '200': '--color__base--orange200',
  '300': '--color__base--orange300',
  '400': '--color__base--orange400',
  '500': '--color__base--orange500',
  '600': '--color__base--orange600',
  '700': '--color__base--orange700',
  '800': '--color__base--orange800',
  '900': '--color__base--orange900',
  '1000': '--color__base--orange1000',
};

const blue: Color = {
  '100': '#f2f9ff',
  '200': '#e5f4ff',
  '300': '#bde3ff',
  '400': '#80caff',
  '500': '#0d99ff',
  '600': '#007be5',
  '700': '#0768cf',
  '800': '#034ac1',
  '900': '#093077',
  '1000': '#0d193f',
};

const blueCSSVariables: Color = {
  '100': '--color__base--blue100',
  '200': '--color__base--blue200',
  '300': '--color__base--blue300',
  '400': '--color__base--blue400',
  '500': '--color__base--blue500',
  '600': '--color__base--blue600',
  '700': '--color__base--blue700',
  '800': '--color__base--blue800',
  '900': '--color__base--blue900',
  '1000': '--color__base--blue1000',
};

const red: Color = {
  '100': '#fff5f5',
  '200': '#ffe2e0',
  '300': '#ffc7c2',
  '400': '#ffafa3',
  '500': '#f24822',
  '600': '#dc3412',
  '700': '#bd2915',
  '800': '#9f1f18',
  '900': '#771208',
  '1000': '#660e0b',
};

const redCSSVariables: Color = {
  '100': '--color__base--red100',
  '200': '--color__base--red200',
  '300': '--color__base--red300',
  '400': '--color__base--red400',
  '500': '--color__base--red500',
  '600': '--color__base--red600',
  '700': '--color__base--red700',
  '800': '--color__base--red800',
  '900': '--color__base--red900',
  '1000': '--color__base--red1000',
};

const grey: Color & { '1100': string; '1200': string } = {
  '100': '#f5f5f5',
  '200': '#eaeaea',
  '300': '#dbdbdb',
  '400': '#b7b7b7',
  '500': '#9b9b9b',
  '600': '#8f8f8f',
  '700': '#686868',
  '800': '#555555',
  '900': '#454545',
  '1000': '#373737',
  '1100': '#2a2a2a',
  '1200': '#151515',
};

const greyCSSVariables: Color & { '1100': string; '1200': string } = {
  '100': '--color__base--grey100',
  '200': '--color__base--grey200',
  '300': '--color__base--grey300',
  '400': '--color__base--grey400',
  '500': '--color__base--grey500',
  '600': '--color__base--grey600',
  '700': '--color__base--grey700',
  '800': '--color__base--grey800',
  '900': '--color__base--grey900',
  '1000': '--color__base--grey1000',
  '1100': '--color__base--grey1100',
  '1200': '--color__base--grey1200',
};

const purple: Color = {
  '100': '#f9f5ff',
  '200': '#f1e5ff',
  '300': '#e4ccff',
  '400': '#d9b8ff',
  '500': '#9747ff',
  '600': '#8638e5',
  '700': '#7c2bda',
  '800': '#681abb',
  '900': '#4b0d87',
  '1000': '#2d0f46',
};

const purpleCSSVariables: Color = {
  '100': '--color__base--purple100',
  '200': '--color__base--purple200',
  '300': '--color__base--purple300',
  '400': '--color__base--purple400',
  '500': '--color__base--purple500',
  '600': '--color__base--purple600',
  '700': '--color__base--purple700',
  '800': '--color__base--purple800',
  '900': '--color__base--purple900',
  '1000': '--color__base--purple1000',
};

const primary: Color = {
  '100': 'rgba(116, 176, 202, 0.1)',
  '200': 'rgba(116, 176, 202, 0.2)',
  '300': 'rgba(116, 176, 202, 0.3)',
  '400': 'rgba(116, 176, 202, 0.4)',
  '500': 'rgba(116, 176, 202, 0.5)',
  '600': 'rgba(116, 176, 202, 0.6)',
  '700': 'rgba(116, 176, 202, 0.7)',
  '800': 'rgba(116, 176, 202, 0.8)',
  '900': 'rgba(116, 176, 202, 0.9)',
  '1000': 'rgba(116, 176, 202, 1)',
};

const primaryCSSVariables: Color = {
  '100': '--color__primary--100',
  '200': '--color__primary--200',
  '300': '--color__primary--300',
  '400': '--color__primary--400',
  '500': '--color__primary--500',
  '600': '--color__primary--600',
  '700': '--color__primary--700',
  '800': '--color__primary--800',
  '900': '--color__primary--900',
  '1000': '--color__primary--1000',
};

const secondary: Color = {
  '100': orange['100'],
  '200': orange['200'],
  '300': orange['300'],
  '400': orange['400'],
  '500': orange['500'],
  '600': orange['600'],
  '700': orange['700'],
  '800': orange['800'],
  '900': orange['900'],
  '1000': orange['1000'],
};

const secondaryCSSVariables: Color = {
  '100': '--color__secondary--100',
  '200': '--color__secondary--200',
  '300': '--color__secondary--300',
  '400': '--color__secondary--400',
  '500': '--color__secondary--500',
  '600': '--color__secondary--600',
  '700': '--color__secondary--700',
  '800': '--color__secondary--800',
  '900': '--color__secondary--900',
  '1000': '--color__secondary--1000',
};

const critical: Omit<Color, '100' | '400' | '700' | '800' | '900' | '1000'> = {
  '200': red['200'],
  '300': red['300'],
  '500': red['500'],
  '600': red['600'],
};

const criticalCSSVariables: Omit<
  Color,
  '100' | '400' | '700' | '800' | '900' | '1000'
> = {
  '200': '--color__critical--200',
  '300': '--color__critical--300',
  '500': '--color__critical--500',
  '600': '--color__critical--600',
};

const positive: Omit<Color, '100' | '400' | '700' | '800' | '900' | '1000'> = {
  '200': green['200'],
  '300': green['300'],
  '500': green['500'],
  '600': green['600'],
};

const positiveCSSVariables: Omit<
  Color,
  '100' | '400' | '700' | '800' | '900' | '1000'
> = {
  '200': '--color__positive--200',
  '300': '--color__positive--300',
  '500': '--color__positive--500',
  '600': '--color__positive--600',
};

const warning: Omit<Color, '100' | '400' | '700' | '800' | '900' | '1000'> = {
  '200': orange['200'],
  '300': orange['300'],
  '500': orange['500'],
  '600': orange['600'],
};

const warningCSSVariables: Omit<
  Color,
  '100' | '400' | '700' | '800' | '900' | '1000'
> = {
  '200': '--color__warning--200',
  '300': '--color__warning--300',
  '500': '--color__warning--500',
  '600': '--color__warning--600',
};

const highlight: Omit<Color, '100' | '400' | '700' | '800' | '900' | '1000'> = {
  '200': blue['200'],
  '300': blue['300'],
  '500': blue['500'],
  '600': blue['600'],
};

const highlightCSSVariables: Omit<
  Color,
  '100' | '400' | '700' | '800' | '900' | '1000'
> = {
  '200': '--color__highlight--200',
  '300': '--color__highlight--300',
  '500': '--color__highlight--500',
  '600': '--color__highlight--600',
};

const neutral: Color & {
  '1100': string;
  '1200': string;
  white: string;
  black: string;
} = {
  white,
  '100': grey['100'],
  '200': grey['200'],
  '300': grey['300'],
  '400': grey['400'],
  '500': grey['500'],
  '600': grey['600'],
  '700': grey['700'],
  '800': grey['800'],
  '900': grey['900'],
  '1000': grey['1000'],
  '1100': grey['1100'],
  '1200': grey['1200'],
  black,
};

const neutralCSSVariables: Color & {
  '1100': string;
  '1200': string;
  white: string;
  black: string;
} = {
  white: '--color__neutral--white',
  '100': '--color__neutral--100',
  '200': '--color__neutral--200',
  '300': '--color__neutral--300',
  '400': '--color__neutral--400',
  '500': '--color__neutral--500',
  '600': '--color__neutral--600',
  '700': '--color__neutral--700',
  '800': '--color__neutral--800',
  '900': '--color__neutral--900',
  '1000': '--color__neutral--1000',
  '1100': '--color__neutral--1100',
  '1200': '--color__neutral--1200',
  black: '--color__neutral--black',
};

export const colors: Colors = {
  white,
  black,
  'primary/100': primary['100'],
  'primary/200': primary['200'],
  'primary/300': primary['300'],
  'primary/400': primary['400'],
  'primary/500': primary['500'],
  'primary/600': primary['600'],
  'primary/700': primary['700'],
  'primary/800': primary['800'],
  'primary/900': primary['900'],
  'primary/1000': primary['1000'],
  'secondary/100': secondary['100'],
  'secondary/200': secondary['200'],
  'secondary/300': secondary['300'],
  'secondary/400': secondary['400'],
  'secondary/500': secondary['500'],
  'secondary/600': secondary['600'],
  'secondary/700': secondary['700'],
  'secondary/800': secondary['800'],
  'secondary/900': secondary['900'],
  'secondary/1000': secondary['1000'],
  'green/100': green['100'],
  'green/200': green['200'],
  'green/300': green['300'],
  'green/400': green['400'],
  'green/500': green['500'],
  'green/600': green['600'],
  'green/700': green['700'],
  'green/800': green['800'],
  'green/900': green['900'],
  'green/1000': green['1000'],
  'orange/100': orange['100'],
  'orange/200': orange['200'],
  'orange/300': orange['300'],
  'orange/400': orange['400'],
  'orange/500': orange['500'],
  'orange/600': orange['600'],
  'orange/700': orange['700'],
  'orange/800': orange['800'],
  'orange/900': orange['900'],
  'orange/1000': orange['1000'],
  'blue/100': blue['100'],
  'blue/200': blue['200'],
  'blue/300': blue['300'],
  'blue/400': blue['400'],
  'blue/500': blue['500'],
  'blue/600': blue['600'],
  'blue/700': blue['700'],
  'blue/800': blue['800'],
  'blue/900': blue['900'],
  'blue/1000': blue['1000'],
  'red/100': red['100'],
  'red/200': red['200'],
  'red/300': red['300'],
  'red/400': red['400'],
  'red/500': red['500'],
  'red/600': red['600'],
  'red/700': red['700'],
  'red/800': red['800'],
  'red/900': red['900'],
  'red/1000': red['1000'],
  'grey/100': grey['100'],
  'grey/200': grey['200'],
  'grey/300': grey['300'],
  'grey/400': grey['400'],
  'grey/500': grey['500'],
  'grey/600': grey['600'],
  'grey/700': grey['700'],
  'grey/800': grey['800'],
  'grey/900': grey['900'],
  'grey/1000': grey['1000'],
  'grey/1100': grey['1100'],
  'grey/1200': grey['1200'],
  'purple/100': purple['100'],
  'purple/200': purple['200'],
  'purple/300': purple['300'],
  'purple/400': purple['400'],
  'purple/500': purple['500'],
  'purple/600': purple['600'],
  'purple/700': purple['700'],
  'purple/800': purple['800'],
  'purple/900': purple['900'],
  'purple/1000': purple['1000'],
  'neutral/100': neutral['100'],
  'neutral/200': neutral['200'],
  'neutral/300': neutral['300'],
  'neutral/400': neutral['400'],
  'neutral/500': neutral['500'],
  'neutral/600': neutral['600'],
  'neutral/700': neutral['700'],
  'neutral/800': neutral['800'],
  'neutral/900': neutral['900'],
  'neutral/1000': neutral['1000'],
  'neutral/1100': neutral['1100'],
  'neutral/1200': neutral['1200'],
  'neutral/black': neutral['black'],
  'neutral/white': neutral['white'],
  'critical/200': critical['200'],
  'critical/300': critical['300'],
  'critical/500': critical['500'],
  'critical/600': critical['600'],
  'positive/200': positive['200'],
  'positive/300': positive['300'],
  'positive/500': positive['500'],
  'positive/600': positive['600'],
  'warning/200': warning['200'],
  'warning/300': warning['300'],
  'warning/500': warning['500'],
  'warning/600': warning['600'],
  'highlight/200': highlight['200'],
  'highlight/300': highlight['300'],
  'highlight/500': highlight['500'],
  'highlight/600': highlight['600'],
};

export const colorsCSSVariables: Colors = {
  white: whiteCSSVariable,
  black: blackCSSVariable,
  'primary/100': primaryCSSVariables['100'],
  'primary/200': primaryCSSVariables['200'],
  'primary/300': primaryCSSVariables['300'],
  'primary/400': primaryCSSVariables['400'],
  'primary/500': primaryCSSVariables['500'],
  'primary/600': primaryCSSVariables['600'],
  'primary/700': primaryCSSVariables['700'],
  'primary/800': primaryCSSVariables['800'],
  'primary/900': primaryCSSVariables['900'],
  'primary/1000': primaryCSSVariables['1000'],
  'secondary/100': secondaryCSSVariables['100'],
  'secondary/200': secondaryCSSVariables['200'],
  'secondary/300': secondaryCSSVariables['300'],
  'secondary/400': secondaryCSSVariables['400'],
  'secondary/500': secondaryCSSVariables['500'],
  'secondary/600': secondaryCSSVariables['600'],
  'secondary/700': secondaryCSSVariables['700'],
  'secondary/800': secondaryCSSVariables['800'],
  'secondary/900': secondaryCSSVariables['900'],
  'secondary/1000': secondaryCSSVariables['1000'],
  'green/100': greenCSSVariables['100'],
  'green/200': greenCSSVariables['200'],
  'green/300': greenCSSVariables['300'],
  'green/400': greenCSSVariables['400'],
  'green/500': greenCSSVariables['500'],
  'green/600': greenCSSVariables['600'],
  'green/700': greenCSSVariables['700'],
  'green/800': greenCSSVariables['800'],
  'green/900': greenCSSVariables['900'],
  'green/1000': greenCSSVariables['1000'],
  'orange/100': orangeCSSVariables['100'],
  'orange/200': orangeCSSVariables['200'],
  'orange/300': orangeCSSVariables['300'],
  'orange/400': orangeCSSVariables['400'],
  'orange/500': orangeCSSVariables['500'],
  'orange/600': orangeCSSVariables['600'],
  'orange/700': orangeCSSVariables['700'],
  'orange/800': orangeCSSVariables['800'],
  'orange/900': orangeCSSVariables['900'],
  'orange/1000': orangeCSSVariables['1000'],
  'blue/100': blueCSSVariables['100'],
  'blue/200': blueCSSVariables['200'],
  'blue/300': blueCSSVariables['300'],
  'blue/400': blueCSSVariables['400'],
  'blue/500': blueCSSVariables['500'],
  'blue/600': blueCSSVariables['600'],
  'blue/700': blueCSSVariables['700'],
  'blue/800': blueCSSVariables['800'],
  'blue/900': blueCSSVariables['900'],
  'blue/1000': blueCSSVariables['1000'],
  'red/100': redCSSVariables['100'],
  'red/200': redCSSVariables['200'],
  'red/300': redCSSVariables['300'],
  'red/400': redCSSVariables['400'],
  'red/500': redCSSVariables['500'],
  'red/600': redCSSVariables['600'],
  'red/700': redCSSVariables['700'],
  'red/800': redCSSVariables['800'],
  'red/900': redCSSVariables['900'],
  'red/1000': redCSSVariables['1000'],
  'grey/100': greyCSSVariables['100'],
  'grey/200': greyCSSVariables['200'],
  'grey/300': greyCSSVariables['300'],
  'grey/400': greyCSSVariables['400'],
  'grey/500': greyCSSVariables['500'],
  'grey/600': greyCSSVariables['600'],
  'grey/700': greyCSSVariables['700'],
  'grey/800': greyCSSVariables['800'],
  'grey/900': greyCSSVariables['900'],
  'grey/1000': greyCSSVariables['1000'],
  'grey/1100': greyCSSVariables['1100'],
  'grey/1200': greyCSSVariables['1200'],
  'purple/100': purpleCSSVariables['100'],
  'purple/200': purpleCSSVariables['200'],
  'purple/300': purpleCSSVariables['300'],
  'purple/400': purpleCSSVariables['400'],
  'purple/500': purpleCSSVariables['500'],
  'purple/600': purpleCSSVariables['600'],
  'purple/700': purpleCSSVariables['700'],
  'purple/800': purpleCSSVariables['800'],
  'purple/900': purpleCSSVariables['900'],
  'purple/1000': purpleCSSVariables['1000'],
  'neutral/100': neutralCSSVariables['100'],
  'neutral/200': neutralCSSVariables['200'],
  'neutral/300': neutralCSSVariables['300'],
  'neutral/400': neutralCSSVariables['400'],
  'neutral/500': neutralCSSVariables['500'],
  'neutral/600': neutralCSSVariables['600'],
  'neutral/700': neutralCSSVariables['700'],
  'neutral/800': neutralCSSVariables['800'],
  'neutral/900': neutralCSSVariables['900'],
  'neutral/1000': neutralCSSVariables['1000'],
  'neutral/1100': neutralCSSVariables['1100'],
  'neutral/1200': neutralCSSVariables['1200'],
  'neutral/black': neutralCSSVariables['black'],
  'neutral/white': neutralCSSVariables['white'],
  'critical/200': criticalCSSVariables['200'],
  'critical/300': criticalCSSVariables['300'],
  'critical/500': criticalCSSVariables['500'],
  'critical/600': criticalCSSVariables['600'],
  'positive/200': positiveCSSVariables['200'],
  'positive/300': positiveCSSVariables['300'],
  'positive/500': positiveCSSVariables['500'],
  'positive/600': positiveCSSVariables['600'],
  'warning/200': warningCSSVariables['200'],
  'warning/300': warningCSSVariables['300'],
  'warning/500': warningCSSVariables['500'],
  'warning/600': warningCSSVariables['600'],
  'highlight/200': highlightCSSVariables['200'],
  'highlight/300': highlightCSSVariables['300'],
  'highlight/500': highlightCSSVariables['500'],
  'highlight/600': highlightCSSVariables['600'],
};
